import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {PlusOutlined, InfoCircleOutlined, EditTwoTone} from '@ant-design/icons';

import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {
  racksList,
  addRack,
  updateRack,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  racksStatusFilter,
} from './racksSlice';
import type {ADD_RACK, RACK_DATA, UPDATE_RACK} from './racks.type';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {rackCategoryList, clearRackCategoryFilters} from '../rackCategory/rackCategorySlice';
import {authStationsList, clearStationsFilters} from '../../station/stations/stationsSlice';
import {debounce} from 'lodash';
import {
  billingStrategy,
  debounceDelay,
  sortbyFilter,
  statusFilter,
  statusFilterOptions,
} from '../../../../common/constants';

const Stations = () => {
  const dispatch = useAppDispatch();
  const racks = useAppSelector((state) => state.racks);
  const userPermission = useAppSelector((state) => state.config.config);

  const [billing, setBilling] = useState('');
  const [isRackModalOpen, setIsRackModalOpen] = useState(false);
  const [isModalUpdate, setIsModalUpdate] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [rackForm] = Form.useForm();
  const rackCategoryOptions = useAppSelector((state) => state.rackCategory.rackCategories.pageData);
  const stationOptions = useAppSelector((state) => state.stations.stations.pageData);

  const [modalData, setModalData] = useState<RACK_DATA>({} as RACK_DATA);

  const handleUpdateRack = (values: {
    name?: string;
    category_id?: number;
    station_id?: number;
    is_active?: boolean;
    billing_strategy?: string;
    flat_price?: number;
    energy_unit_price?: number;
  }) => {
    const data: UPDATE_RACK = {
      name: values.name,
      category_id: values.category_id,
      station_id: values.station_id,
      is_active: values.is_active,
    };
    if (values.billing_strategy !== undefined) {
      data.extra_details = {
        billing_config: {
          billingStrategy: values.billing_strategy,
          flatPrice: values.flat_price,
          energyUnitPrice: values.energy_unit_price,
        },
      };
    }

    const payload = checkFormUpdate(data, modalData);

    if (Object.keys(payload).length > 0) {
      dispatch(updateRack({payload: payload, id: modalData.id}))
        .unwrap()
        .then(() => {
          handleAddRackModalClose();
          dispatch(
            racksList({
              filters: {
                page_size: racks.racks.page_size,
                page_number: racks.racks.page_number,
                sort_by: racks.racks.sort_by,
                ascending: racks.racks.ascending,
                search: racks.racks.search,
              },
            }),
          );
        });
    } else {
      message.warning('No field Changed');
    }
  };

  const handleAddRack = (values: {
    name: string;
    asset_id: string;
    station_id: number;
    category_id: number;
    billing_strategy?: string;
    flat_price?: number;
    energy_unit_price?: number;
  }) => {
    const payload = {} as ADD_RACK;
    payload.name = values.name;
    payload.station_id = values.station_id;
    payload.asset_id = values.asset_id;
    payload.category_id = values.category_id;
    if (values.billing_strategy !== undefined) {
      payload.extra_details = {
        billing_config: {
          billingStrategy: values.billing_strategy,
          flatPrice:
            values.billing_strategy === billingStrategy.SOC_BASED ? null : values.flat_price,
          energyUnitPrice:
            values.billing_strategy === billingStrategy.FLAT ? null : values.energy_unit_price,
        },
      };
    }
    dispatch(addRack(payload))
      .unwrap()
      .then(() => {
        handleAddRackModalClose();
        dispatch(
          racksList({
            filters: {
              page_size: racks.racks.page_size,
              page_number: racks.racks.page_number,
              sort_by: racks.racks.sort_by,
              ascending: racks.racks.ascending,
              search: racks.racks.search,
            },
          }),
        );
      });
  };

  const handleAddRackModalClose = () => {
    setBilling('');
    setIsRackModalOpen(false);
    rackForm.resetFields();
    dispatch(clearRackCategoryFilters());
    dispatch(clearStationsFilters());
  };

  const handleStationBouncing = debounce((e) => {
    dispatch(authStationsList({search: e, is_active: true}));
  }, debounceDelay);

  const handleRackCategoryBouncing = debounce((e) => {
    dispatch(rackCategoryList({search: e, is_active: true}));
  });

  useEffect(() => {
    dispatch(
      racksList({
        filters: {
          page_size: racks.racks.page_size,
          page_number: racks.racks.page_number,
          sort_by: racks.racks.sort_by,
          ascending: racks.racks.ascending,
          search: racks.racks.search,
          is_active: racks.racks.is_active,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    racks.racks.page_number,
    racks.racks.page_size,
    racks.racks.search,
    racks.racks.is_active,
    racks.racks.sort_by,
    racks.racks.ascending,
  ]);

  const columns: ColumnsType<RACK_DATA> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return <>{(racks.racks.page_number - 1) * racks.racks.page_size + index + 1}</>;
      },
    },
    {
      title: 'Cabinet Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'Category Name',
      dataIndex: 'category_name',
    },

    {
      title: ' Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active ? (
          <Tag color='success'>Active</Tag>
        ) : (
          <Tag color='error'>Inactive</Tag>
        );
      },
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {userPermission.permissions.update_rack && (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'blue', marginInline: '10px'}}
                onClick={() => {
                  setIsModalUpdate(true);
                  setModalData(record);
                  dispatch(rackCategoryList({is_active: true}));
                  dispatch(authStationsList({is_active: true}));
                  rackForm.setFieldsValue({
                    name: record.name,
                    category_id: record.category_id,
                    station_id: record.station_id,
                    is_active: record.is_active,
                    billing_strategy:
                      record.extra_details !== null
                        ? record.extra_details.billing_config.billingStrategy
                        : undefined,
                    flat_price:
                      record.extra_details !== null
                        ? record.extra_details.billing_config.flatPrice
                        : undefined,
                    energy_unit_price:
                      record.extra_details !== null
                        ? record.extra_details.billing_config.energyUnitPrice
                        : undefined,
                  });
                  record.extra_details !== null
                    ? setBilling(record.extra_details.billing_config.billingStrategy)
                    : '';
                  setIsRackModalOpen(true);
                }}
              />
            )}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  return (
    <div>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px 1fr 1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={racks.racks.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            racks.racks.is_active === undefined
              ? statusFilter.ALL
              : racks.racks.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(racksStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(racksStatusFilter(undefined));
            } else {
              dispatch(racksStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {userPermission.permissions.add_rack ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setIsModalUpdate(false);
                dispatch(rackCategoryList({is_active: true}));
                dispatch(authStationsList({is_active: true}));
                setIsRackModalOpen(true);
              }}
            >
              Add Cabinet
            </Button>
          ) : null}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: racks.racks.totalDataCount,
            current: racks.racks.page_number,
            pageSize: racks.racks.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== racks.racks.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={racks.isLoading}
        size='middle'
        dataSource={racks.racks.pageData}
        scroll={{x: undefined, y: '69vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== racks.racks.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== racks.racks.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      {isInfoModalOpen && (
        <Modal
          title={`${modalData?.name}`}
          open={isInfoModalOpen}
          onCancel={() => {
            setIsInfoModalOpen(false);
          }}
          footer={null}
          width={800}
        >
          <Descriptions bordered column={2} labelStyle={{fontWeight: 'bolder'}}>
            <Descriptions.Item label='Cabinet Name'>{modalData?.name}</Descriptions.Item>
            <Descriptions.Item label='Cabinet Id'>{modalData?.id}</Descriptions.Item>

            <Descriptions.Item label='Is Active'>
              {modalData?.is_active ? (
                <Tag color='success'>Active</Tag>
              ) : (
                <Tag color='error'> Inactive</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label='Created By'>{modalData?.created_by}</Descriptions.Item>
            <Descriptions.Item label='Created At'>{modalData?.created_at}</Descriptions.Item>
            <Descriptions.Item label='Updated By'>{modalData?.updated_by}</Descriptions.Item>
            <Descriptions.Item label='Updated At'>{modalData?.updated_at}</Descriptions.Item>
            <Descriptions.Item label='Station Name'>{modalData?.station_name}</Descriptions.Item>
            <Descriptions.Item label=' Cabinet Category Name'>
              {modalData?.category_name}
            </Descriptions.Item>
            {modalData?.extra_details !== null && (
              <>
                <Descriptions.Item label='Billing Strategy'>
                  {modalData.extra_details.billing_config.billingStrategy}
                </Descriptions.Item>
                {modalData.extra_details.billing_config.billingStrategy !== 'soc_based' && (
                  <Descriptions.Item label='Flat  Price'>
                    {modalData.extra_details.billing_config.flatPrice}
                  </Descriptions.Item>
                )}
                {modalData.extra_details.billing_config.billingStrategy !== 'flat' && (
                  <Descriptions.Item label='Energy Unit Price '>
                    {modalData.extra_details.billing_config.energyUnitPrice}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        </Modal>
      )}
      <Modal
        title={isModalUpdate ? 'Update Cabinet' : 'Add Cabinet'}
        open={isRackModalOpen}
        maskClosable={false}
        onCancel={() => {
          handleAddRackModalClose();
        }}
        confirmLoading={racks.isLoading}
        okText={isModalUpdate ? 'Update Cabinet' : 'Add Cabinet'}
        onOk={() => {
          rackForm
            .validateFields()
            .then((values) => {
              //updateRackForm.resetFields();
              isModalUpdate ? handleUpdateRack(values) : handleAddRack(values);
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='add_rack' form={rackForm}>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='name'
                    rules={[
                      {required: true, message: 'Please fill Cabinet Name '},
                      {min: 4, message: 'Minimum length is 4 characters'},
                    ]}
                    label='Cabinet Name'
                    labelCol={{span: 24}}
                  >
                    <Input autoComplete='off' placeholder='Cabinet Name' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='category_id'
                    rules={[{required: true, message: 'Please fill catagorId'}]}
                    label='Catagory'
                    labelCol={{span: 24}}
                  >
                    <Select
                      placeholder='Please Select'
                      showSearch
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      onSearch={(e: string) => {
                        if (e.length > 3) {
                          handleRackCategoryBouncing(e);
                        }
                      }}
                      notFoundContent={null}
                      //loading={roles.isLoading}
                    >
                      {rackCategoryOptions.map((x, i) => (
                        <Select.Option key={i} value={x.id}>
                          {x.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='station_id'
                    rules={[{required: true, message: 'Please fill Station Id'}]}
                    label='Station'
                    labelCol={{span: 24}}
                  >
                    <Select
                      showSearch
                      placeholder='Select Station'
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      onSearch={(e: string) => {
                        if (e.length > 3) {
                          handleStationBouncing(e);
                        }
                      }}
                      notFoundContent={null}
                      //loading={roles.isLoading}
                    >
                      {stationOptions.map((x, i) => (
                        <Select.Option key={i} value={x.id}>
                          {x.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  {!isModalUpdate ? (
                    <Form.Item
                      name='asset_id'
                      rules={[{required: true, message: 'Please fill asset Id '}]}
                      label=' Asset Id'
                      labelCol={{span: 24}}
                    >
                      <Input autoComplete='off' placeholder='Asset Id' />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name='is_active'
                      label='Is Active'
                      labelCol={{span: 24}}
                      valuePropName='checked'
                    >
                      <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                    </Form.Item>
                  )}
                </Col>
              </Row>
              {userPermission.config.billing_config.allowRackBilling && (
                <>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name='billing_strategy'
                        label='Billing Strategy'
                        labelCol={{span: 24}}
                      >
                        <Select
                          showSearch
                          placeholder='Select Billing Strategy'
                          defaultActiveFirstOption={false}
                          showArrow={true}
                          allowClear
                          filterOption={false}
                          onChange={(e) => {
                            setBilling(e);
                          }}
                        >
                          {userPermission.config.billing_strategy_options.map((x, i) => (
                            <Select.Option key={i} value={x.value}>
                              {x.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    {billing === billingStrategy.FLAT ||
                    billing === billingStrategy.FLAT_EXTENDED ? (
                      <Col span={11}>
                        <Form.Item
                          name='flat_price'
                          rules={[
                            {
                              pattern: new RegExp('^[0-9]'),
                              required:
                                billing === billingStrategy.FLAT ||
                                billing === billingStrategy.FLAT_EXTENDED
                                  ? true
                                  : false,
                              message: 'Enter only numeric value',
                            },
                          ]}
                          label='Flat Price'
                          labelCol={{span: 24}}
                        >
                          <InputNumber style={{width: '100%'}} autoComplete='off' />
                        </Form.Item>
                      </Col>
                    ) : null}
                    {billing === billingStrategy.SOC_BASED ||
                    billing === billingStrategy.FLAT_EXTENDED ? (
                      <Col span={11} offset={billing === billingStrategy.SOC_BASED ? 0 : 2}>
                        <Form.Item
                          name='energy_unit_price'
                          rules={[
                            {
                              pattern: new RegExp('^[0-9]'),
                              required:
                                billing === billingStrategy.SOC_BASED ||
                                billing === billingStrategy.FLAT_EXTENDED
                                  ? true
                                  : false,

                              message: 'Enter only numeric value',
                            },
                          ]}
                          label='Energy Unit Price'
                          labelCol={{span: 24}}
                        >
                          <InputNumber style={{width: '100%'}} autoComplete='off' />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>
                </>
              )}
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Stations;
